package com.elan.edubridge.data.firebase

import com.elan.edubridge.data.firebase.externals.Auth
import com.elan.edubridge.data.firebase.externals.Firestore
import com.elan.edubridge.data.firebase.externals.Functions
import com.elan.edubridge.data.firebase.externals.GoogleAuthProvider
import com.elan.edubridge.data.firebase.externals.User
import com.elan.edubridge.data.firebase.externals.collection
import com.elan.edubridge.data.firebase.externals.createUserWithEmailAndPassword
import com.elan.edubridge.data.firebase.externals.deleteUser
import com.elan.edubridge.data.firebase.externals.doc
import com.elan.edubridge.data.firebase.externals.getAdditionalUserInfo
import com.elan.edubridge.data.firebase.externals.getDoc
import com.elan.edubridge.data.firebase.externals.getDocs
import com.elan.edubridge.data.firebase.externals.serverTimestamp
import com.elan.edubridge.data.firebase.externals.setDoc
import com.elan.edubridge.data.firebase.externals.signInWithEmailAndPassword
import com.elan.edubridge.data.firebase.externals.signInWithPopup
import com.elan.edubridge.data.firebase.externals.signOut
import com.elan.edubridge.data.firebase.externals.httpsCallable
import com.elan.edubridge.shared.data.datasource.AccountDataSource
import com.elan.edubridge.shared.data.firebase.FirebaseNaming
import com.elan.edubridge.shared.domain.model.AcademyMembership
import com.elan.edubridge.shared.domain.model.AccountDeletionBlocker
import com.elan.edubridge.shared.domain.model.AccountDeletionBlockerReason
import com.elan.edubridge.shared.domain.model.AccountDeletionRetention
import com.elan.edubridge.shared.domain.model.AccountDeletionState
import com.elan.edubridge.shared.domain.model.AccountDeletionStatus
import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.AuthResult
import com.elan.edubridge.shared.domain.model.BirthDate
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.GuardianConsentStatus
import com.elan.edubridge.shared.domain.model.ProfileUpdateResult
import com.elan.edubridge.shared.domain.model.UserAccount
import com.elan.edubridge.shared.domain.model.UserRole
import com.elan.edubridge.shared.domain.policy.AgePolicy
import com.elan.edubridge.shared.domain.policy.SignupAgeVerdict
import kotlin.js.json
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.await
import kotlinx.coroutines.withContext

/**
 * [AccountDataSource]의 Firebase Auth + Firestore(named DB `edubridge`) 구현.
 *
 * **핵심 주의(§S4)**: [currentAccount]를 `auth.currentUser` 그대로 매핑하면 새로고침마다 로그인
 * 화면으로 튕긴다. Firebase 웹 SDK는 영속 세션을 비동기로 복원하므로 [awaitAuthReady]가 끝나기
 * 전에는 `currentUser`가 항상 null이다 — 반드시 첫 렌더 이전에 [awaitAuthReady]를 기다린 뒤
 * [currentAccount]를 불러야 한다(호출 순서는 `AccountRepositoryImpl.restoreSession()`이 보장한다).
 */
class FirebaseAccountDataSource(
    private val auth: Auth,
    private val firestore: Firestore,
    private val functions: Functions,
) : AccountDataSource {

    private companion object {
        const val GOOGLE_SIGNUP_DETAILS_REQUIRED = "Google 신규 가입은 회원가입 화면에서 이름과 생년월일을 입력해 주세요."

        fun minimumSignupAgeMessage(): String = "만 ${AgePolicy.MINIMUM_SIGNUP_AGE}세 이상만 가입할 수 있어요."
    }

    override suspend fun login(email: String, password: String): AuthResult = try {
        val credential = signInWithEmailAndPassword(auth, email, password).await()
        AuthResult(account = loadAccount(credential.user))
    } catch (e: Throwable) {
        AuthResult(errorMessage = AuthErrorMapper.toKoreanMessage(e.jsErrorCode()))
    }

    override suspend fun register(
        email: String,
        password: String,
        name: String,
        birthDate: String,
        ageGroup: AgeGroup,
    ): AuthResult {
        val normalizedName = name.trim()
        if (normalizedName.isBlank()) {
            return AuthResult(errorMessage = "이름을 입력해 주세요.")
        }
        val normalizedBirthDate = BirthDate.normalize(birthDate)
            ?: return AuthResult(errorMessage = "생년월일을 YYYY-MM-DD 형식으로 올바르게 입력해 주세요.")
        when (AgePolicy.validateSignupBirthDate(birthDate, todayIsoDate())) {
            SignupAgeVerdict.MALFORMED -> return AuthResult(errorMessage = "생년월일을 YYYY-MM-DD 형식으로 올바르게 입력해 주세요.")
            SignupAgeVerdict.IN_FUTURE -> return AuthResult(errorMessage = "생년월일은 오늘 이후 날짜일 수 없어요.")
            SignupAgeVerdict.TOO_YOUNG -> return AuthResult(errorMessage = minimumSignupAgeMessage())
            SignupAgeVerdict.ELIGIBLE -> Unit
        }
        return try {
            val credential = createUserWithEmailAndPassword(auth, email, password).await()
            val user = credential.user

            // 공개 가입 계정은 항상 STUDENT이고 globalRoles 키 자체를 넣지 않는다 — rules가
            // `!('globalRoles' in request.resource.data)`를 요구한다(firebase/firestore.rules:44-45).
            val payload = json(
                "name" to normalizedName,
                "birthDate" to normalizedBirthDate,
                "baseRole" to UserRole.STUDENT.name,
                "ageGroup" to ageGroup.name,
                "consentStatus" to GuardianConsentStatus.NOT_REQUIRED.name,
                "createdAt" to serverTimestamp(),
            )
            setDoc(doc(firestore, FirebaseNaming.USERS, user.uid), payload).await()

            AuthResult(
                account = UserAccount(
                    id = user.uid,
                    email = user.email ?: email,
                    name = normalizedName,
                    birthDate = normalizedBirthDate,
                    baseRole = UserRole.STUDENT,
                    globalRoles = emptySet(),
                    ageGroup = ageGroup,
                    consentStatus = GuardianConsentStatus.NOT_REQUIRED,
                ),
            )
        } catch (e: Throwable) {
            AuthResult(errorMessage = AuthErrorMapper.toKoreanMessage(e.jsErrorCode()))
        }
    }

    /**
     * Google 팝업 로그인. 웹은 팝업이 토큰 교환까지 스스로 끝내므로 [idToken]을 쓰지 않는다
     * (계약 인자의 의미는 [AccountDataSource.signInWithGoogle] KDoc 참고).
     *
     * 기존 프로필은 그대로 로그인하고 절대 수정하지 않는다. 프로필이 없는 계정은 회원가입 화면에서
     * 넘긴 이름·생년월일이 모두 유효할 때만 새 문서를 만든다([ensureUserDocument]). 로그인 화면에서
     * 처음 선택한 Google 계정은 문서를 만들지 않고 로그아웃시켜 회원가입으로 안전하게 되돌린다.
     * 팝업을 닫거나(`auth/popup-closed-by-user`) 두 번째 팝업 요청에 밀려난 경우(`auth/cancelled-popup-request`)는
     * 사용자가 스스로 취소한 것이라 오류 문구 없이 실패만 알린다 — 호출부가 오류 배너를 띄우지 않는다.
     */
    override suspend fun signInWithGoogle(
        idToken: String?,
        registrationName: String?,
        birthDate: String?,
    ): AuthResult {
        var authenticatedWithGoogle = false
        return try {
            val credential = signInWithPopup(auth, GoogleAuthProvider()).await()
            val user = credential.user
            val isNewGoogleUser = getAdditionalUserInfo(credential)?.isNewUser == true
            authenticatedWithGoogle = true
            when (ensureUserDocument(user, registrationName, birthDate)) {
                UserDocumentResult.EXISTING, UserDocumentResult.CREATED -> AuthResult(account = loadAccount(user))
                UserDocumentResult.DETAILS_REQUIRED -> {
                    clearAuthSession()
                    AuthResult(errorMessage = GOOGLE_SIGNUP_DETAILS_REQUIRED)
                }
                UserDocumentResult.TOO_YOUNG -> {
                    discardRejectedNewGoogleUser(user, isNewGoogleUser)
                    AuthResult(errorMessage = minimumSignupAgeMessage())
                }
            }
        } catch (e: CancellationException) {
            if (authenticatedWithGoogle) clearAuthSession()
            throw e
        } catch (e: Throwable) {
            if (authenticatedWithGoogle) clearAuthSession()
            val code = e.jsErrorCode()?.substringAfterLast('/')
            if (code == "popup-closed-by-user" || code == "cancelled-popup-request") {
                AuthResult()
            } else {
                AuthResult(errorMessage = AuthErrorMapper.toKoreanMessage(e.jsErrorCode()))
            }
        }
    }

    /**
     * `edubridge_users/{uid}` 문서가 없을 때만 회원가입에서 받은 값을 저장한다. 기존 문서는 어떤
     * 경우에도 수정하지 않아 Google 공급자 이름이 EduBridge 프로필을 덮어쓰지 않는다.
     */
    private suspend fun ensureUserDocument(
        user: User,
        registrationName: String?,
        birthDate: String?,
    ): UserDocumentResult {
        val existing = getDoc(doc(firestore, FirebaseNaming.USERS, user.uid)).await()
        if (existing.exists()) return UserDocumentResult.EXISTING

        val name = registrationName?.trim().orEmpty()
        val normalizedBirthDate = birthDate?.let(BirthDate::normalize)
        if (name.isBlank() || normalizedBirthDate == null) return UserDocumentResult.DETAILS_REQUIRED
        when (AgePolicy.validateSignupBirthDate(normalizedBirthDate, todayIsoDate())) {
            SignupAgeVerdict.ELIGIBLE -> Unit
            SignupAgeVerdict.TOO_YOUNG -> return UserDocumentResult.TOO_YOUNG
            SignupAgeVerdict.MALFORMED, SignupAgeVerdict.IN_FUTURE -> return UserDocumentResult.DETAILS_REQUIRED
        }

        val payload = json(
            "name" to name,
            "birthDate" to normalizedBirthDate,
            "baseRole" to UserRole.STUDENT.name,
            "ageGroup" to AgeGroup.UNKNOWN.name,
            "consentStatus" to GuardianConsentStatus.NOT_REQUIRED.name,
            "createdAt" to serverTimestamp(),
        )
        setDoc(doc(firestore, FirebaseNaming.USERS, user.uid), payload).await()
        return UserDocumentResult.CREATED
    }

    private enum class UserDocumentResult { EXISTING, CREATED, DETAILS_REQUIRED, TOO_YOUNG }

    /** 신규 Google 인증 계정만 지운다. 기존 계정은 프로필 문서가 없더라도 로그아웃만 한다. */
    private suspend fun discardRejectedNewGoogleUser(user: User, isNewGoogleUser: Boolean) {
        withContext(NonCancellable) {
            if (isNewGoogleUser) {
                try {
                    deleteUser(user).await()
                } catch (_: Throwable) {
                    // 계정 삭제가 실패해도 만 14세 미만 사용자가 세션을 유지하면 안 된다.
                }
            }
            clearAuthSession()
        }
    }

    override suspend fun awaitAuthReady() {
        auth.authStateReady().await()
    }

    override suspend fun currentAccount(): UserAccount? {
        val user = auth.currentUser ?: return null
        return loadAccount(user)
    }

    override suspend fun forceRefreshToken() {
        auth.currentUser?.getIdToken(true)?.await()
    }

    override suspend fun memberships(userId: String): List<AcademyMembership> = try {
        val snapshot = getDocs(
            collection(firestore, FirebaseNaming.USERS, userId, UserSubcollections.MEMBERSHIPS),
        ).await()
        snapshot.docs.map { toAcademyMembership(it.data()) }
    } catch (e: Throwable) {
        // 복구 가능한 실패(권한 없음·네트워크)는 예외를 던지지 않는다 — EduConnectDataSource와
        // 동일한 실패 규약을 계정 경계에도 일관되게 적용한다.
        emptyList()
    }

    override suspend fun logout() {
        signOut(auth).await()
    }

    /** 서버가 caller uid만 쓰도록 고정한 `edubridge-updateMyProfile` 호출이다. */
    override suspend fun updateMyProfile(name: String): ProfileUpdateResult = try {
        val response = httpsCallable(functions, callableName("updateMyProfile"))(
            json("name" to name),
        ).await()
        val updatedName = stringOrNull(response.data, "name")
        if (updatedName == null) {
            ProfileUpdateResult(
                failure = CommandFailure.INTERNAL,
                errorMessage = GENERIC_COMMAND_ERROR_MESSAGE,
            )
        } else {
            ProfileUpdateResult(name = updatedName)
        }
    } catch (e: CancellationException) {
        throw e
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.updateMyProfile failed", e)
        ProfileUpdateResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun accountDeletionStatus(): AccountDeletionState = invokeAccountDeletion("getAccountDeletionStatus")

    override suspend fun requestAccountDeletion(reason: String?): AccountDeletionState {
        val payload = json()
        if (reason != null) payload["reason"] = reason
        return invokeAccountDeletion("requestAccountDeletion", payload)
    }

    override suspend fun cancelAccountDeletion(): AccountDeletionState = invokeAccountDeletion("cancelAccountDeletion")

    /** Profile read/write fails after OAuth only in a broken signup/login, so do not retain that auth session. */
    private suspend fun clearAuthSession() {
        withContext(NonCancellable) {
            try {
                signOut(auth).await()
            } catch (_: Throwable) {
                // 원래 실패(또는 취소)를 보존한다. 다음 복원에서 남은 세션을 재시도하지 않도록 최선만 다한다.
            }
        }
    }

    private suspend fun invokeAccountDeletion(name: String, payload: dynamic = json()): AccountDeletionState = try {
        val response = httpsCallable(functions, callableName(name))(payload).await()
        toAccountDeletionState(response.data)
    } catch (e: CancellationException) {
        throw e
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.$name failed", e)
        AccountDeletionState(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
            blockers = accountDeletionBlockersOrEmpty(e.asDynamic().details),
        )
    }

    private fun callableName(name: String): String = "${FirebaseNaming.FUNCTIONS_PREFIX}$name"

    private fun toAccountDeletionState(data: dynamic): AccountDeletionState = AccountDeletionState(
        status = accountDeletionStatusOrDefault(stringOrNull(data, "status")),
        requestedAt = stringOrNull(data, "requestedAt"),
        scheduledPurgeAt = stringOrNull(data, "scheduledPurgeAt"),
        cancelledAt = stringOrNull(data, "cancelledAt"),
        completedAt = stringOrNull(data, "completedAt"),
        graceDays = intOrDefault(data, "graceDays", 0),
        retentionNotice = accountDeletionRetentionsOrEmpty(data),
        blockers = accountDeletionBlockersOrEmpty(data),
    )

    private fun accountDeletionStatusOrDefault(value: String?): AccountDeletionStatus =
        AccountDeletionStatus.entries.firstOrNull { it.name == value } ?: AccountDeletionStatus.NONE

    private fun accountDeletionRetentionsOrEmpty(data: dynamic): List<AccountDeletionRetention> =
        stringArrayField(data, "retentionNotice").mapNotNull { value ->
            AccountDeletionRetention.entries.firstOrNull { it.name == value }
        }

    private fun accountDeletionBlockersOrEmpty(data: dynamic): List<AccountDeletionBlocker> {
        val raw = data?.blockers
        if (js("!Array.isArray(raw)") as Boolean) return emptyList()
        return raw.unsafeCast<Array<dynamic>>().mapNotNull { blocker ->
            val academyId = stringOrNull(blocker, "academyId") ?: return@mapNotNull null
            val academyName = stringOrNull(blocker, "academyName") ?: return@mapNotNull null
            val reason = AccountDeletionBlockerReason.entries.firstOrNull {
                it.name == stringOrNull(blocker, "reason")
            } ?: return@mapNotNull null
            AccountDeletionBlocker(academyId = academyId, academyName = academyName, reason = reason)
        }
    }

    private fun stringArrayField(data: dynamic, field: String): List<String> {
        val raw = if (data == null) null else data[field]
        if (js("!Array.isArray(raw)") as Boolean) return emptyList()
        return raw.unsafeCast<Array<dynamic>>().mapNotNull { value ->
            if (jsTypeOf(value) == "string") value as String else null
        }
    }

    /**
     * Auth `User` + `edubridge_users/{uid}` 문서 + custom claim을 합쳐 [UserAccount]를 만든다.
     * 문서 읽기가 실패(권한 없음 등)해도 인증 자체는 성공했으므로 null 대신 최소 계정으로 폴백한다.
     */
    private suspend fun loadAccount(user: User): UserAccount {
        val globalRoles = try {
            val claims = user.getIdTokenResult(false).await().claims
            val isSuperAdmin = js("claims != null && claims.superAdmin === true") as Boolean
            if (isSuperAdmin) setOf(UserRole.SUPER_ADMIN) else emptySet()
        } catch (e: Throwable) {
            emptySet()
        }

        val snapshot = try {
            getDoc(doc(firestore, FirebaseNaming.USERS, user.uid)).await()
        } catch (e: Throwable) {
            null
        }

        return if (snapshot != null && snapshot.exists()) {
            toUserAccount(snapshot.data(), uid = user.uid, email = user.email, globalRoles = globalRoles)
        } else {
            UserAccount(
                id = user.uid,
                email = user.email ?: "",
                name = "",
                baseRole = UserRole.STUDENT,
                globalRoles = globalRoles,
                ageGroup = AgeGroup.UNKNOWN,
                consentStatus = GuardianConsentStatus.NOT_REQUIRED,
            )
        }
    }
}

/** 서버와 Firestore 규칙의 연령 판정 기준일과 같은 서울 달력일이다. */
internal fun todayIsoDate(): String = js(
    """(() => {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit'
        });
        const parts = formatter.formatToParts(new Date());
        const value = (type) => parts.find((part) => part.type === type).value;
        return value('year') + '-' + value('month') + '-' + value('day');
    })()""",
) as String
